import { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard, Library, Users, LogOut, CalendarClock, CalendarRange, KeyRound,
  ChevronDown, TrendingUp, Briefcase, UserCog, Settings, PanelLeftClose, PanelLeft,
} from 'lucide-react';
import { NotificationBell } from './NotificationBell';
import { SessionKeeper } from './SessionKeeper';
import { useAuthStore } from '@/store/authStore';
import { roleLabel } from '@/lib/permissions';
import { isOn, useFeatures } from '@/lib/features';
import { NAV_RULES, ACCOUNT_PAGE, navFor } from '@/lib/roleRouting';
import api, { broadcastLogout } from '@/api/client';
import toast from 'react-hot-toast';
import { cn } from '@/lib/cn';
import { Logo } from '@/components/brand/Logo';
import { Avatar } from '@/components/ui';

type NavItem = { label: string; icon: typeof LayoutDashboard; href: string };

/**
 * The menu's icons. Who sees which item is decided in one place, lib/roleRouting.ts
 * (navFor): today's items for staff, My interviews alone for an Interviewer.
 */
const ICONS: Record<string, typeof LayoutDashboard> = {
  '/admin/dashboard': LayoutDashboard,
  '/admin/positions': Briefcase,
  '/admin/library': Library,
  '/admin/candidates': Users,
  '/admin/interviews': CalendarRange,
  '/admin/analytics': TrendingUp,
  '/admin/my-interviews': CalendarClock,
  '/admin/team': UserCog,
  '/admin/settings': Settings,
};

const navItems: NavItem[] = NAV_RULES.map((r) => ({ label: r.label, href: r.href, icon: ICONS[r.href] ?? LayoutDashboard }));

/** The topbar's title for pages that are not in the menu. */
const TITLES: { href: string; label: string }[] = [...navItems, { href: ACCOUNT_PAGE, label: 'Account' }];

/** Below Tailwind's md (768 px). */
const PHONE_QUERY = '(max-width: 767px)';

/** Whether the window is phone-width now, following resizes and rotation. */
function usePhoneWidth(): boolean {
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia(PHONE_QUERY).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(PHONE_QUERY);
    const update = () => setNarrow(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  return narrow;
}

export default function AdminLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const features = useFeatures();
  const navigate = useNavigate();
  const location = useLocation();
  /*
   * PHONE WIDTH (with "Interviews" on, so the release changes nothing until it is). An
   * Interviewer's whole app — My interviews and the interview room — is used on a phone, and
   * an open 256 px menu in the flow left the page about 70 px at 375. Below 768 px the menu
   * is a drawer over the page instead: closed at first, taking no width, opened from the top
   * bar's toggle, closed on Escape, on the backdrop and on every navigation. At 768 px and
   * above the frame is exactly today's.
   */
  const phone = usePhoneWidth() && isOn(features, 'INTERVIEWS_ENABLED');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);
  // In the drawer the menu is always shown in full.
  const expanded = phone || sidebarOpen;

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname, phone]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    drawerRef.current?.querySelector<HTMLElement>('a, button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setDrawerOpen(false);
      toggleRef.current?.focus();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const shown = new Set(navFor(user?.role, features));
  const visibleNav = navItems.filter((item) => shown.has(item.href));
  // Everyone has the bell. An Interviewer's holds only their own interview notices (added to
  // a panel, moved, cancelled, a scorecard due): the server allow-lists /api/notifications for
  // them and scopes it to rounds they are on now (backend interviews/notices.ts), and each
  // notice opens the round. It is how they are told in the app; no new polling was added.
  // Everyone may change their password once "Team rules" is on (the route answers 'not
  // found' before that, and the account page says how it is done today).
  const showAccount = isOn(features, 'TEAM_RULES');

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch { /* ignore */ }
    // With "Refresh sign-in" on, this also empties the query cache (lib/session.ts), so
    // the next person on this tab is shown nothing of this one's.
    logout();
    // The auth store is per-tab while the refresh cookie is shared, so without this
    // every other open tab keeps rendering the signed-in UI after an explicit sign
    // out — on a shared machine that reads as "still logged in".
    broadcastLogout();
    navigate('/auth/login');
    toast.success('Logged out successfully');
  };

  const fullName = `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || 'User';

  const menu = (
    <>
        {/* Logo */}
        <div className="flex items-center h-16 px-4 flex-shrink-0">
          {expanded ? <Logo /> : <Logo variant="mark" />}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 py-3 space-y-1 overflow-y-auto">
          {visibleNav.map((item) => {
            const isActive = location.pathname.startsWith(item.href);
            return (
              <NavLink
                key={item.href}
                to={item.href}
                // A tap on the page you are on still closes the drawer.
                onClick={phone ? () => setDrawerOpen(false) : undefined}
                title={!expanded ? item.label : undefined}
                className={cn(
                  'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group relative',
                  isActive ? 'bg-primary-50 text-primary-700 font-semibold' : 'text-ink-500 hover:bg-ink-50 hover:text-ink-900',
                )}
              >
                {isActive && (
                  <motion.div
                    layoutId="activeNav"
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 rounded-r-full bg-primary-500"
                    transition={{ type: 'spring', bounce: 0.2, duration: 0.4 }}
                  />
                )}
                <item.icon className={cn('w-5 h-5 flex-shrink-0 relative z-10', isActive ? 'text-primary-500' : 'text-ink-400 group-hover:text-ink-600')} />
                <AnimatePresence>
                  {expanded && (
                    <motion.span
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="text-sm font-medium relative z-10 whitespace-nowrap"
                    >
                      {item.label}
                    </motion.span>
                  )}
                </AnimatePresence>
              </NavLink>
            );
          })}
        </nav>

        {/* User section */}
        <div className="px-3 py-3 border-t border-[#ECE7E3]">
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-ink-50 transition-colors"
          >
            <Avatar name={fullName} size="sm" />
            <AnimatePresence>
              {expanded && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 text-left min-w-0">
                  <div className="text-sm font-semibold text-ink-900 truncate">{fullName}</div>
                  <div className="text-xs text-ink-400 truncate">{roleLabel(user?.role)}</div>
                </motion.div>
              )}
            </AnimatePresence>
            {expanded && <ChevronDown className={cn('w-4 h-4 text-ink-300 transition-transform flex-shrink-0', userMenuOpen && 'rotate-180')} />}
          </button>

          <AnimatePresence>
            {userMenuOpen && expanded && (
              <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="mt-1 bg-white rounded-xl border border-[#ECE7E3] overflow-hidden shadow-card"
              >
                {showAccount && (
                  <Link
                    to={ACCOUNT_PAGE}
                    onClick={() => setUserMenuOpen(false)}
                    className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-ink-700 hover:bg-ink-50 transition-colors border-b border-[#F1ECE8]"
                  >
                    <KeyRound className="w-4 h-4 text-ink-400" />
                    Account and password
                  </Link>
                )}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign out
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
    </>
  );

  return (
    <div className="flex h-screen bg-surface-50 overflow-hidden">
      {/* ── Sidebar ──────────────────────────────────────── */}
      {phone ? (
        drawerOpen ? (
          <>
            <div data-testid="menu-backdrop" aria-hidden="true" className="fixed inset-0 z-40 bg-ink-900/30" onClick={() => setDrawerOpen(false)} />
            <aside id="admin-menu" ref={drawerRef} className="fixed inset-y-0 left-0 z-50 flex w-64 max-w-[85vw] flex-col bg-white border-r border-[#ECE7E3] shadow-card">
              {menu}
            </aside>
          </>
        ) : null
      ) : (
        <motion.aside
          animate={{ width: sidebarOpen ? 256 : 76 }}
          transition={{ duration: 0.3, ease: 'easeInOut' }}
          className="relative flex flex-col bg-white border-r border-[#ECE7E3] z-30 flex-shrink-0"
        >
          {menu}
        </motion.aside>
      )}

      {/* ── Main Content ──────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        {/* no-print: this bar is app furniture — search, notifications, the signed-in
            user. It printed "ADMIN" and a bell onto exported reports because the print
            stylesheet hid nav and aside but this is a <header>. Marked at source so no
            future stylesheet has to guess the tag. */}
        <header className={phone
          ? 'no-print h-16 flex items-center gap-4 px-4 bg-surface-50/80 backdrop-blur border-b border-[#ECE7E3] flex-shrink-0'
          : 'no-print h-16 flex items-center gap-4 px-6 bg-surface-50/80 backdrop-blur border-b border-[#ECE7E3] flex-shrink-0'}
        >
          <button
            ref={toggleRef}
            onClick={() => (phone ? setDrawerOpen((o) => !o) : setSidebarOpen(!sidebarOpen))}
            className="p-2 rounded-lg text-ink-400 hover:text-ink-700 hover:bg-ink-50 transition-colors"
            aria-label="Toggle sidebar"
            aria-expanded={phone ? drawerOpen : undefined}
            aria-controls={phone && drawerOpen ? 'admin-menu' : undefined}
          >
            {(phone ? drawerOpen : sidebarOpen) ? <PanelLeftClose className="w-5 h-5" /> : <PanelLeft className="w-5 h-5" />}
          </button>

          <div className="flex-1">
            <h1 className="font-display text-sm font-bold text-ink-500 uppercase tracking-wide">
              {TITLES.find((n) => location.pathname.toLowerCase().startsWith(n.href))?.label ?? 'Admin'}

            </h1>
          </div>

          <NotificationBell />
        </header>

        {/* Re-reads the signed-in role with "Refresh sign-in" on; renders nothing. */}
        <SessionKeeper />

        {/* Page content */}
        <main className={phone ? 'flex-1 overflow-y-auto p-4 bg-surface-50' : 'flex-1 overflow-y-auto p-6 lg:p-8 bg-surface-50'}>
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            /*
             * Was max-w-7xl (1280px), which left a third of a 1920px screen empty on
             * every admin page and squeezed the candidate table — ten columns including
             * rank, score, percentile, waiting time and integrity — into less room than
             * the browser was offering.
             *
             * Still capped rather than full-bleed: this layout also carries the candidate
             * report, which is a reading document, and unbounded lines on a wide monitor
             * are their own problem. 1600 fills a 1440 screen exactly and leaves a modest
             * margin at 1920. Blocks that genuinely want a narrow measure — prose, the
             * video player — set their own width and are unaffected.
             */
            className="max-w-[1600px] mx-auto"
          >
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
